/**
 * Guide de coexistence ancien CMS ↔ CMS avancé.
 * Affiché dans les deux shells pour clarifier quand utiliser quel outil.
 */
import { useVisibleT } from "@/lib/cms/cms-new-i18n";

export function CmsCoexistenceGuide() {
  const vt = useVisibleT();
  return (
    <div className="prose prose-sm max-w-none text-slate-700">
      <h2 className="text-base font-semibold text-slate-900">
        {vt("bo.cms.coexistenceGuide.mainTitle", "Deux CMS, un seul site")}
      </h2>
      <p>
        {vt(
          "bo.cms.coexistenceGuide.intro",
          "Les deux interfaces coexistent pour préserver les contenus existants tout en permettant une migration progressive vers le constructeur de pages avancé.",
        )}
      </p>

      <h3 className="text-sm font-semibold text-slate-900">
        {vt("bo.cms.coexistenceGuide.legacyTitle", "Ancien CMS")}
      </h3>
      <ul className="list-disc pl-5">
        <li>
          {vt(
            "bo.cms.coexistenceGuide.legacyItem1",
            "Pages structurées par type : accueil, contact, sélections, expertise…",
          )}
        </li>
        <li>
          {vt(
            "bo.cms.coexistenceGuide.legacyItem2",
            "Données historiques déjà en place",
          )}
        </li>
        <li>
          {vt(
            "bo.cms.coexistenceGuide.legacyItem3",
            "E-shop, authentification d'œuvres, formulaires legacy",
          )}
        </li>
      </ul>

      <h3 className="text-sm font-semibold text-slate-900">
        {vt("bo.cms.coexistenceGuide.advancedTitle", "CMS avancé")}
      </h3>
      <ul className="list-disc pl-5">
        <li>
          {vt(
            "bo.cms.coexistenceGuide.advancedItem1",
            "Pages libres construites par blocs (Hero, colonnes, catalogue…)",
          )}
        </li>
        <li>
          {vt(
            "bo.cms.coexistenceGuide.advancedItem2",
            "Menus CMS avancé, médiathèque, SEO par langue",
          )}
        </li>
        <li>
          {vt(
            "bo.cms.coexistenceGuide.advancedItem3",
            "Idéal pour les nouvelles pages ou la refonte progressive",
          )}
        </li>
      </ul>

      <h3 className="text-sm font-semibold text-slate-900">
        {vt("bo.cms.coexistenceGuide.sharedTitle", "Partagé entre les deux")}
      </h3>
      <p>
        {vt(
          "bo.cms.coexistenceGuide.sharedText",
          "Apparence (palette, header/footer), réglages généraux, langues, catalogue raisonné et médiathèque sont communs.",
        )}
      </p>

      <h3 className="text-sm font-semibold text-slate-900">
        {vt("bo.cms.coexistenceGuide.routingTitle", "Routage public")}
      </h3>
      <p>
        {vt("bo.cms.coexistenceGuide.routingIntro1", "Quand le")}{" "}
        <strong>
          {vt("bo.cms.coexistenceGuide.routingAdvancedMode", "mode avancé")}
        </strong>{" "}
        {vt(
          "bo.cms.coexistenceGuide.routingIntro2",
          "est activé dans les réglages, les pages CMS avancé",
        )}{" "}
        <strong>
          {vt("bo.cms.coexistenceGuide.routingPublished", "publiées")}
        </strong>{" "}
        {vt(
          "bo.cms.coexistenceGuide.routingIntro3",
          "ont priorité sur un slug donné. Si aucune page CMS avancé ne correspond, le site affiche la page de l'ancien CMS.",
        )}
      </p>
      <ul className="list-disc pl-5">
        <li>
          <strong>
            {vt("bo.cms.coexistenceGuide.routingHomeLabel", "Accueil")}
          </strong>{" "}
          {vt(
            "bo.cms.coexistenceGuide.routingHomeText",
            ": page CMS avancé définie comme accueil (SEO) ou homepage legacy selon la résolution.",
          )}
        </li>
        <li>
          <strong>
            {vt("bo.cms.coexistenceGuide.routingMenusLabel", "Menus")}
          </strong>{" "}
          {vt(
            "bo.cms.coexistenceGuide.routingMenusText",
            ": menus CMS avancé utilisés s'ils sont configurés ; sinon navigation legacy.",
          )}
        </li>
        <li>
          <strong>
            {vt(
              "bo.cms.coexistenceGuide.routingConflictLabel",
              "Conflits de slug",
            )}
          </strong>{" "}
          {vt(
            "bo.cms.coexistenceGuide.routingConflictText",
            ": si un slug CMS avancé et un slug legacy coexistent, le CMS avancé gagne lorsque le mode avancé est actif — vérifie la colonne « conflit » dans la liste des pages.",
          )}
        </li>
      </ul>

      <h3 className="text-sm font-semibold text-slate-900">
        {vt("bo.cms.coexistenceGuide.checklistTitle", "Checklist migration")}
      </h3>
      <ol className="list-decimal pl-5">
        <li>
          {vt(
            "bo.cms.coexistenceGuide.checklistItem1",
            "Activer le mode avancé (Réglages)",
          )}
        </li>
        <li>
          {vt(
            "bo.cms.coexistenceGuide.checklistItem2",
            "Configurer apparence et langues",
          )}
        </li>
        <li>
          {vt(
            "bo.cms.coexistenceGuide.checklistItem3",
            "Créer et publier les pages CMS avancé",
          )}
        </li>
        <li>
          {vt(
            "bo.cms.coexistenceGuide.checklistItem4",
            "Définir la page d'accueil et les slugs SEO par langue",
          )}
        </li>
        <li>
          {vt(
            "bo.cms.coexistenceGuide.checklistItem5",
            "Configurer les menus primary / footer",
          )}
        </li>
        <li>
          {vt(
            "bo.cms.coexistenceGuide.checklistItem6",
            "Tester chaque langue sur le site public",
          )}
        </li>
        <li>
          {vt(
            "bo.cms.coexistenceGuide.checklistItem7",
            "Conserver l'ancien CMS pour le contenu non encore migré",
          )}
        </li>
      </ol>
    </div>
  );
}
